import React, { FunctionComponent, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "../ui/drawer";
import { Button } from "../ui/button";
import { FaCopy, FaCheck } from "react-icons/fa";
import { useCopyToClipboard } from "react-use";
import { useConfiguratorContext } from "../../hooks/configurator/configurator-context";
import { useSaveConfiguration } from "../../hooks/useSaveConfiguration";
import { Loader2 } from "lucide-react";
import { useMediaQuery } from "../../hooks/use-media-query";
import { cn } from "../../lib/utils";

function SaveCodeContent({
  className,
  isSaving,
  error,
  code,
  configUrl,
  codeCopied,
  linkCopied,
  handleCopyCode,
  handleRetry,
  handleClose,
}: {
  className?: string;
  isSaving: boolean;
  error: string | undefined;
  code: string | undefined;
  configUrl: string | undefined;
  codeCopied: boolean;
  linkCopied: boolean;
  handleCopyCode: (value: string | undefined, type: "code" | "link") => void;
  handleRetry: () => void;
  handleClose: () => void;
}) {
  return (
    <div className={cn("flex flex-col items-center mt-4", className)}>
      {isSaving && (
        <div className="flex flex-col items-center gap-4 py-10 w-full">
          <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
          <p className="text-sm md:text-base text-gray-600">
            Generating your Garage Code…
          </p>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden max-w-sm">
            <div
              className="h-full bg-black/80 animate-pulse"
              style={{ width: "60%" }}
            ></div>
          </div>
        </div>
      )}

      {!isSaving && error && (
        <div className="w-full max-w-xl bg-red-50 border border-red-200 text-red-800 rounded-md p-4">
          <p className="text-sm mb-4">{error}</p>
          <div className="flex gap-2 justify-center">
            <Button onClick={handleRetry}>Try again</Button>
            <Button variant="ghost" onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      )}

      {!isSaving && !error && code && (
        <>
          <div
            className="flex flex-col sm:flex-row justify-between items-center w-full mb-4 bg-gray-100 hover:bg-gray-200 cursor-pointer rounded-lg p-2"
            onClick={() => handleCopyCode(code, "code")}
          >
            <div className="flex">
              {code.split("").map((char, index) => (
                <div
                  key={index}
                  className="p-2 text-sm font-mono flex items-center justify-center w-10 h-10 border-r-2"
                >
                  {char}
                </div>
              ))}
            </div>
            <Button
              onClick={() => handleCopyCode(code, "code")}
              variant="ghost"
              size="sm"
              className="text-sm px-4 py-2 flex items-center"
            >
              {codeCopied ? (
                <>
                  <FaCheck className="mr-2 text-green-500" /> Code copied
                </>
              ) : (
                <>
                  <FaCopy className="mr-2" /> Copy code
                </>
              )}
            </Button>
          </div>

          <div
            className="flex flex-col sm:flex-row justify-between items-center w-full mb-4 bg-gray-100 hover:bg-gray-200 cursor-pointer rounded-lg p-2"
            onClick={() => handleCopyCode(configUrl, "link")}
          >
            <p className="text-sm text-left p-2 w-full bg-transparent border-none break-all">
              {configUrl}
            </p>
            <Button
              onClick={() => handleCopyCode(configUrl, "link")}
              variant="ghost"
              className="ml-4 text-sm px-4 py-2 flex items-center"
            >
              {linkCopied ? (
                <>
                  <FaCheck className="mr-2 text-green-500" /> Link copied
                </>
              ) : (
                <>
                  <FaCopy className="mr-2" /> Copy link
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

const SaveCodeDialog: FunctionComponent = () => {
  const [open, setOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [state, copyToClipboard] = useCopyToClipboard();
  const [codeCopied, setCodeCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const configurator = useConfiguratorContext();
  const { configuratorName } = configurator;

  const { save, code } = useSaveConfiguration(
    configuratorName,
    configurator.getAllSelectedOptionIds()
  );

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setIsSaving(true);
    setError(undefined);
    setCodeCopied(false);
    setLinkCopied(false);
    save()
      .catch((e) => {
        if (!cancelled)
          setError("Failed to generate your Garage Code. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setIsSaving(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const configUrl =
    typeof window !== "undefined" && code
      ? `${window.location.protocol}//${window.location.host}/garage-code/${code}`
      : `/garage-code/${code}`;

  // Handle code copy and reset after a delay
  const handleCopyCode = (value: string | undefined, type: "code" | "link") => {
    if (!value) return;
    copyToClipboard(value);

    if (type === "code") {
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000); // Reset after 2 seconds
    } else if (type === "link") {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000); // Reset after 2 seconds
    }
  };

  const handleRetry = () => {
    setIsSaving(true);
    setError(undefined);
    save()
      .catch(() =>
        setError("Failed to generate your Garage Code. Please try again.")
      )
      .finally(() => setIsSaving(false));
  };

  const handleClose = () => setOpen(false);

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className="w-full bg-white border-gray-300 hover:bg-gray-50 text-gray-900 rounded-md"
          >
            Save Config
          </Button>
        </DialogTrigger>
        <DialogContent className="p-4 md:p-6 max-w-full sm:max-w-xl overflow-hidden ">
          <DialogHeader className="text-center">
            <DialogTitle className="text-xl sm:text-2xl ">
              The Garage Code for your configuration
            </DialogTitle>
            <DialogDescription>
              Access or share your configuration anytime using the code or link
              below.
            </DialogDescription>
          </DialogHeader>
          <SaveCodeContent
            isSaving={isSaving}
            error={error}
            code={code}
            configUrl={configUrl}
            codeCopied={codeCopied}
            linkCopied={linkCopied}
            handleCopyCode={handleCopyCode}
            handleRetry={handleRetry}
            handleClose={handleClose}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button
          variant="outline"
          className="w-full bg-white border-gray-300 hover:bg-gray-50 text-gray-900 rounded-md"
        >
          Save Config
        </Button>
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader className="text-center">
          <DrawerTitle className="text-xl sm:text-2xl ">
            The Garage Code for your configuration
          </DrawerTitle>
          <DrawerDescription>
            Access or share your configuration anytime using the code or link
            below.
          </DrawerDescription>
        </DrawerHeader>
        <SaveCodeContent
          className="px-4"
          isSaving={isSaving}
          error={error}
          code={code}
          configUrl={configUrl}
          codeCopied={codeCopied}
          linkCopied={linkCopied}
          handleCopyCode={handleCopyCode}
          handleRetry={handleRetry}
          handleClose={handleClose}
        />
        <DrawerFooter className="pt-2">
          <DrawerClose asChild>
            <Button variant="outline">Close</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default SaveCodeDialog;
